import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
const mainColor = '#1B344A';
const secondaryColor = '#3E5B74';

function identifyCarrier(trackingNumber) {
  const num = trackingNumber.replace(/\s/g, '');

  if (/^1Z[A-Z0-9]{16}$/i.test(num)) return 'ups';
  if (/^(94|93|92|91|95)\d{18,20}$/.test(num)) return 'usps';
  if (/^[A-Z]{2}\d{9}US$/i.test(num)) return 'usps';
  if (/^\d{12}$/.test(num) || /^\d{15}$/.test(num)) return 'fedex';
  if (/^\d{20}$/.test(num)) return num.startsWith('92') ? 'usps' : 'fedex';

  return null;
}

export default function AccountOrderHistory() {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState([]);
  const [hoveredInfoOrderId, setHoveredInfoOrderId] = useState(null);
  const colorsByStatus = new Map([
    ['pending', '#f0ad4e'],
    ['processing', '#5bc0de'],
    ['shipped', '#0275d8'],
    ['delivered', '#67c067ff'],
    ['canceled', '#d9534f'],
  ])
  useEffect(() => {
    async function fetchOrders() {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/orders`, { withCredentials: true });
        setOrders(res.data.data || []);
      } catch (err) {
        setError(err.message || 'Error fetching orders');
      } finally {
        setLoading(false);
      }
    }
    fetchOrders();
  }, []);

  const toggleOrder = (orderId) => {
    setExpandedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  if (loading) return <p>Loading orders...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;
  if (!orders || orders.length === 0) return <p>No orders found.</p>;

  return (
    <>
      <h2 className="account-order-history-title">Order History</h2>
      <div className="orders-list">
        {orders.map((o) => {
          const isExpanded = expandedOrderIds.includes(o._id);
          return (
            <div key={o._id} className="order-card" style={{ borderBottom: '1px solid #ddd', padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <div className="account-order-history-id-container">
                  <strong style={{ fontSize: '1.25rem' }}>{o.orderId}</strong>
                  <div style={{ fontSize: 12, color: '#666' }}>{new Date(o.createdAt).toLocaleString()}</div>

                  {o?.payment?.checkoutUrl && o.status === 'pending' && (
                    <div style={{ height: 'fit-content', padding: '1rem 0 0.5rem 0' }}>
                      <a href={o.payment.checkoutUrl} className="account-order-history-complete-order-button" target="_blank" rel="noopener noreferrer">
                        Complete Payment
                      </a>
                    </div>
                  )}

                </div>


                <div className="account-order-history-total">

                  <h3>{(o.total / 100).toFixed(2)} {o.currency}</h3>
                  <div style={{ position: 'relative', fontSize: '1rem', backgroundColor: colorsByStatus.get(o.status) || '#666', color: '#fff', padding: '4px 8px', borderRadius: '1rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {o.status === 'pending' && (
                      <span
                        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'help', color: '#fff' }}
                        onMouseEnter={() => setHoveredInfoOrderId(o._id)}
                        onMouseLeave={() => setHoveredInfoOrderId(null)}
                      >
                        <i className="bi bi-info-circle-fill" style={{ fontSize: '1rem' }} />
                        <small
                          style={{
                            display: hoveredInfoOrderId === o._id ? 'block' : 'none',
                            position: 'absolute',
                            bottom: '110%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: 220,
                            padding: '8px 10px',
                            borderRadius: 8,
                            backgroundColor: '#fff',
                            color: '#333',
                            border: '1px solid rgba(0,0,0,0.15)',
                            boxShadow: '0 8px 18px rgba(0,0,0,0.15)',
                            whiteSpace: 'normal',
                            fontSize: 12,
                            zIndex: 100,
                            textAlign: 'center',
                          }}
                        >
                          Your order is pending payment. Session link expires within 24hrs.
                        </small>
                      </span>
                    )}
                    {o.status === 'processing' && (
                      <span
                        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'help', color: '#fff' }}
                        onMouseEnter={() => setHoveredInfoOrderId(o._id)}
                        onMouseLeave={() => setHoveredInfoOrderId(null)}
                      >
                        <i className="bi bi-info-circle-fill" style={{ fontSize: '1rem' }} />
                        <small
                          style={{
                            display: hoveredInfoOrderId === o._id ? 'block' : 'none',
                            position: 'absolute',
                            bottom: '110%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            minWidth: 220,
                            padding: '8px 10px',
                            borderRadius: 8,
                            backgroundColor: '#fff',
                            color: '#333',
                            border: '1px solid rgba(0,0,0,0.15)',
                            boxShadow: '0 8px 18px rgba(0,0,0,0.15)',
                            whiteSpace: 'normal',
                            fontSize: 12,
                            zIndex: 100,
                            textAlign: 'center',
                          }}
                        >
                          We have received your payment and are currently processing your order. You will receive a notification once it has shipped. Please allow 1-2 business days for processing time.
                        </small>
                      </span>
                    )}
                    {o.status}
                  </div>

                </div>
              </div>

              {(o.status === 'shipped' || o.status === 'delivered') && o?.shipping?.statusHistory &&
                <div className="account-order-history-shipping-label" style={{ color: mainColor, fontWeight: 'bold' }}>
                  Shipping Label:
                  <i style={{ fontWeight: "normal", marginLeft: '0.25rem' }}>
                    {o.shipping.statusHistory?.[0]}
                  </i><button style={{ marginLeft: '0.5rem',backgroundColor: 'transparent', borderRadius: '8px', color: secondaryColor, border: `1px solid ${secondaryColor}`, cursor: 'pointer', padding: '0.25rem 0.5rem', fontWeight: 'bold' }} onClick={() => {
                    navigator.clipboard.writeText(o.shipping.statusHistory?.[0])
                    alert('Tracking number copied to clipboard.');
                    }}>
                    <i className="bi bi-copy"></i>
                  </button>

                </div>
              }
              <button
                type="button"
                className="account-order-history-toggle-button"
                onClick={() => toggleOrder(o._id)}
              >
                {isExpanded ? <>Hide order details <i className="bi bi-chevron-contract"></i></> : <>View order details <i className="bi bi-chevron-expand"></i></>}
              </button>

              {isExpanded && (
                <div style={{ marginTop: 12, padding: 12, background: '#f9f9f9', borderRadius: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <div style={{ marginBottom: 10 }}>
                    <strong>Items</strong>
                  </div>
                  {Array.isArray(o.items) && o.items.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: 6 }}>Product</th>
                          <th style={{ textAlign: 'right', padding: 6 }}>Qty</th>
                          <th style={{ textAlign: 'right', padding: 6 }}>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o.items.map((item) => (
                          <tr key={item.productId || item.name} style={{ border: "1px solid #f0f0f0" }}>
                            <td style={{ padding: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span
                                style={{
                                  backgroundImage: `url(${item.imageUrl})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  backgroundRepeat: 'no-repeat',
                                  width: 50,
                                  height: 50,
                                  display: 'inline-block',
                                  borderRadius: '2rem',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}
                              ></span>
                              <span>{item.name}</span>
                            </td>
                            <td style={{ padding: 6, textAlign: 'right' }}>{item.quantity}</td>
                            <td style={{ padding: 6, textAlign: 'right' }}>${(item.lineTotal / 100).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ margin: 0 }}>No items available for this order.</p>
                  )}

                  <div style={{ marginTop: 16, textAlign: 'right', fontSize: 14 }}>
                    <div><strong>Shipping Cost:</strong> {o.status === 'pending' ? 'Pending' : `$${(o.shippingCost || 0) / 100}`}</div>
                    <div><strong>Tax:</strong> {o.status === 'pending' ? 'Pending' : `$${(o.tax || 0) / 100}`}</div>
                    <div><strong>{o.status === 'pending' || o.status === 'processing' ? 'Subtotal: ' : "Total: "}</strong> ${((o.total - o.tax - o.shippingCost) || 0) / 100}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
